import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Switch } from '@appkit4/react-components/switch';
import { Panel } from '@appkit4/react-components/panel';
import { List, ListItem } from '@appkit4/react-components/list';
import { Loading } from '@appkit4/react-components/loading';
import { Tabs, Tab } from '@appkit4/react-components/tabs';
import useGenericErrorToast from '../../helpers/useGenericErrorToast';
import useToast from '../../helpers/useToast';
import './Settings.scss';
import { AppRoutes } from '../../Constants';
import useLocale from '../../locale/useLocale';
import {
    getGetV1IdentityUserInfoQueryKey,
    getGetV1UserPreferencesQueryKey,
    useGetV1IdentityUserInfo,
    useGetV1UserPreferences,
    usePutV1UserPreferences,
    PutV1UserPreferencesMutationError,
    useGetV1PolicyCookiesVersion,
} from '../../api/generated/endpoint';
import { useQueryClient } from '@tanstack/react-query';
import { UserPreferencesRequest } from '../../api/generated/models';
import { ThemeMode, useStoredDarkMode } from '../../helpers/DarkModeDetector';
import useTitle from '../../helpers/useTitle';

const Settings = () => {
    const showToast = useToast();
    const { l } = useLocale();
    useTitle(l('_settings'));

    const showGenericErrorToast = useGenericErrorToast();
    const queryClient = useQueryClient();

    const [isUpdatingAnalytics, setIsUpdatingAnalytics] =
        useState<boolean>(false);
    const [isUpdatingMarketing, setIsUpdatingMarketing] =
        useState<boolean>(false);
    const [isUpdatingEmailNotifications, setIsUpdatingEmailNotifications] =
        useState<boolean>(false);

    const {
        data: userPreferences,
        isLoading: loadingUserPreferences,
        error: userPreferencesError,
    } = useGetV1UserPreferences();

    useEffect(() => {
        if (userPreferencesError) {
            showGenericErrorToast(userPreferencesError);
        }
    }, [userPreferencesError, showGenericErrorToast]);

    const { data: cookiePolicyVersion, isLoading: loadingCookiePolicy } =
        useGetV1PolicyCookiesVersion();

    const UpdateUserPreferencesRequest = usePutV1UserPreferences({
        mutation: {
            onMutate: (variables: { data: UserPreferencesRequest }) => {
                const {
                    acceptedAnalyticsCookiesVersion,
                    acceptedMarketingCookiesVersion,
                    emailNotifications,
                } = variables.data;
                if (
                    acceptedAnalyticsCookiesVersion !=
                    userPreferences?.acceptedAnalyticsCookiesVersion
                ) {
                    setIsUpdatingAnalytics(true);
                }
                if (
                    acceptedMarketingCookiesVersion !=
                    userPreferences?.acceptedMarketingCookiesVersion
                ) {
                    setIsUpdatingMarketing(true);
                }
                if (emailNotifications != null) {
                    setIsUpdatingEmailNotifications(true);
                }
            },
            onSuccess: (data: UserPreferencesRequest) => {
                const {
                    acceptedAnalyticsCookiesVersion,
                    acceptedMarketingCookiesVersion,
                    emailNotifications,
                } = data;

                showToast(l('_success'), l('_preferencesSaved'), 'success');
                setTimeout(() => {
                    if (acceptedAnalyticsCookiesVersion != null) {
                        setIsUpdatingAnalytics(false);
                    }
                    if (acceptedMarketingCookiesVersion != null) {
                        setIsUpdatingMarketing(false);
                    }
                    if (emailNotifications != null) {
                        setIsUpdatingEmailNotifications(false);
                    }
                }, 500); // sometimes bugs when changed too quick)
                queryClient.invalidateQueries({
                    queryKey: getGetV1UserPreferencesQueryKey(),
                });
                queryClient.invalidateQueries({
                    queryKey: getGetV1IdentityUserInfoQueryKey(),
                });
            },
            onError: (error: PutV1UserPreferencesMutationError) => {
                showGenericErrorToast(error);
            },
        },
    });

    const sendUpdatePreferencesRequest = (
        updateOptions: UserPreferencesRequest
    ) => {
        UpdateUserPreferencesRequest.mutate({
            data: updateOptions,
        });
    };

    return (
        <>
            <div className="ap-simple-panel-container">
                <Panel className="panel">
                    <h1 className="my-4">{l('_settings')}</h1>
                    <h2 className="ap-mt-spacing-6 ap-typography-heading">
                        {l('_cookies')}
                    </h2>
                    <p>{l('_cookieInformation')}</p>
                    <div className="row w-100 ap-mt-spacing-6">
                        <div className="col-auto ms-1">
                            <label htmlFor="analyticsCookiePreferenceSwitch">
                                {l('_analyticsCookies')}
                            </label>
                        </div>
                        <div className="col-auto ms-auto">
                            {loadingUserPreferences ||
                            isUpdatingAnalytics ||
                            loadingCookiePolicy ? (
                                <Loading
                                    loadingType="circular"
                                    indeterminate
                                    compact
                                />
                            ) : (
                                <Switch
                                    inputId="analyticsCookiePreferenceSwitch"
                                    checked={
                                        (userPreferences?.acceptedAnalyticsCookiesVersion ??
                                            -1) > -1
                                    }
                                    onChange={(newValue) =>
                                        sendUpdatePreferencesRequest({
                                            tcVersionAccepted: null,
                                            acceptedAnalyticsCookiesVersion:
                                                newValue
                                                    ? cookiePolicyVersion?.latestVersion ??
                                                      null
                                                    : -1,
                                            acceptedMarketingCookiesVersion:
                                                userPreferences?.acceptedMarketingCookiesVersion,
                                            emailNotifications: null,
                                        })
                                    }
                                />
                            )}
                        </div>
                    </div>
                    <div className="row w-100 ap-mt-spacing-6">
                        <div className="col-auto ms-1">
                            <label htmlFor="marketingCookiesSwitch">
                                {l('_marketingCookies')}
                            </label>
                        </div>
                        <div className="col-auto ms-auto">
                            {loadingUserPreferences ||
                            isUpdatingMarketing ||
                            loadingCookiePolicy ? (
                                <Loading
                                    loadingType="circular"
                                    indeterminate
                                    compact
                                />
                            ) : (
                                <Switch
                                    inputId="marketingCookiesSwitch"
                                    checked={
                                        (userPreferences?.acceptedMarketingCookiesVersion ??
                                            -1) > -1
                                    }
                                    onChange={(newValue) =>
                                        sendUpdatePreferencesRequest({
                                            tcVersionAccepted: null,
                                            acceptedAnalyticsCookiesVersion:
                                                userPreferences?.acceptedAnalyticsCookiesVersion,
                                            acceptedMarketingCookiesVersion:
                                                newValue
                                                    ? cookiePolicyVersion?.latestVersion ??
                                                      null
                                                    : -1,
                                            emailNotifications: null,
                                        })
                                    }
                                />
                            )}
                        </div>
                    </div>
                </Panel>
            </div>
        </>
    );
};

export default Settings;
